import { apiGet, apiPost, apiPut, apiDelete } from './client'
import type { ApiResponse } from '@crm/shared'

export interface RecordRow {
  id: string
  seq_id: number
  object_id: string
  tenant_id: string
  owner_id: string
  data: Record<string, unknown>
  version: number
  is_deleted: number
  created_by: string
  created_by_name: string
  updated_by: string
  created_at: string
  updated_at: string
}

export interface RecordListResult {
  data: RecordRow[]
  pagination: {
    next_cursor: string | null
    has_more: boolean
    total_count?: number
    page?: number
    page_size?: number
  }
}

export async function fetchRecords(objectId: string, params?: Record<string, string>): Promise<RecordListResult> {
  const searchParams = new URLSearchParams()
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v) searchParams.set(k, v)
    }
  }
  const qs = searchParams.toString()
  return apiGet<RecordListResult>(`/objects/${objectId}/records${qs ? `?${qs}` : ''}`)
}

export async function fetchRecord(objectId: string, recordId: string): Promise<ApiResponse<RecordRow>> {
  return apiGet<ApiResponse<RecordRow>>(`/objects/${objectId}/records/${recordId}`)
}

export async function createRecord(objectId: string, data: Record<string, unknown>, ownerId?: string) {
  return apiPost<ApiResponse<RecordRow>, { data: Record<string, unknown>; owner_id?: string }>(
    `/objects/${objectId}/records`,
    { data, owner_id: ownerId },
  )
}

export async function updateRecord(objectId: string, recordId: string, payload: { data: Record<string, unknown>; version: number }) {
  return apiPut<ApiResponse<RecordRow>, { data: Record<string, unknown>; version: number }>(
    `/objects/${objectId}/records/${recordId}`,
    payload,
  )
}

export async function deleteRecord(objectId: string, recordId: string) {
  return apiDelete<ApiResponse<{ success: boolean }>>(`/objects/${objectId}/records/${recordId}`)
}

export async function restoreRecord(objectId: string, recordId: string) {
  return apiPost<ApiResponse<{ success: boolean }>, Record<string, never>>(
    `/objects/${objectId}/records/${recordId}/restore`,
    {},
  )
}

export async function batchCreateRecords(objectId: string, records: Array<{ data: Record<string, unknown>; owner_id?: string }>) {
  return apiPost<ApiResponse<{ success_count: number; fail_count: number }>, { records: typeof records }>(
    `/objects/${objectId}/records/batch`,
    { records },
  )
}

export async function batchDeleteRecords(objectId: string, recordIds: string[]) {
  return apiDelete<ApiResponse<{ success_count: number; fail_count: number }>>(
    `/objects/${objectId}/records/batch`,
    { record_ids: recordIds },
  )
}

export async function fetchRelatedRecords(
  objectId: string,
  recordId: string,
  fieldId: string,
  cursor?: string,
): Promise<RecordListResult> {
  const qs = cursor ? `?cursor=${cursor}` : ''
  return apiGet<RecordListResult>(`/objects/${objectId}/records/${recordId}/related/${fieldId}${qs}`)
}

export async function fetchOwnedRecords(
  objectId: string,
  recordId: string,
  targetObjectId: string,
  cursor?: string,
): Promise<RecordListResult> {
  const params = new URLSearchParams()
  params.set('target_object_id', targetObjectId)
  if (cursor) params.set('cursor', cursor)
  return apiGet<RecordListResult>(`/objects/${objectId}/records/${recordId}/owned-records?${params.toString()}`)
}

export interface ChangeListResult {
  data: Array<Record<string, unknown>>
  pagination: {
    next_cursor: string | null
    has_more: boolean
  }
}

export async function fetchRecordChanges(
  objectId: string,
  recordId: string,
  cursor?: string,
): Promise<ChangeListResult> {
  const qs = cursor ? `?cursor=${cursor}` : ''
  return apiGet<ChangeListResult>(`/objects/${objectId}/records/${recordId}/changes${qs}`)
}

export async function convertRecord(
  objectId: string,
  recordId: string,
  targetType: string,
  data?: Record<string, unknown>,
) {
  return apiPost<ApiResponse<{ opportunityId?: string; customerId?: string; leadRecordId?: string; opportunityRecordId?: string }>, { target_type: string; data?: Record<string, unknown> }>(
    `/objects/${objectId}/records/${recordId}/convert`,
    { target_type: targetType, data },
  )
}
